import React from 'react'
import Select from 'react-select'
import PeriodPickerField from 'pages/parts/PeriodPicker'
import { returnMinDate } from 'helpers/helpers'
import { ANALYTICS_DATE_RANGE_DAYS } from '../AnalyticsDatePicker'
import { PERIOD_PRESETS, DEFAULT_PRESET_IDS, buildPeriod, formatPeriodLabel } from './analyticsDates'

// Единый вид селектов в фильтрах аналитики
const selectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: '38px',
		borderRadius: '10px',
		borderColor: state.isFocused ? 'var(--bs-primary)' : 'var(--dark-border, #dfe5ec)',
		boxShadow: state.isFocused ? '0 0 0 3px rgba(var(--bs-primary-rgb), 0.12)' : 'none',
		'&:hover': { borderColor: 'var(--bs-primary)' },
	}),
	valueContainer: (base) => ({ ...base, padding: '0 10px' }),
	multiValue: (base) => ({ ...base, borderRadius: '6px', background: 'rgba(var(--bs-primary-rgb), 0.1)' }),
	multiValueLabel: (base) => ({ ...base, color: 'var(--bs-primary)', fontWeight: 600 }),
	menuPortal: (base) => ({ ...base, zIndex: 1060 }),
	menu: (base) => ({ ...base, borderRadius: '10px', overflow: 'hidden' }),
	option: (base, state) => ({
		...base,
		background: state.isSelected ? 'var(--bs-primary)' : state.isFocused ? 'rgba(var(--bs-primary-rgb), 0.08)' : undefined,
	}),
}

/**
 * Селект фильтра. Опции — { id, name }. Для «всех» передайте allLabel: появится опция с id -1.
 * Мультиселект: isMulti, value — массив id, пустой массив = «все».
 */
export function FilterSelect({ options = [], value, onChange, allLabel, isMulti = false, getOptionLabel, placeholder, isDisabled, isClearable }) {
	const list = allLabel && !isMulti ? [{ id: -1, name: allLabel }, ...options] : options
	const labelOf = getOptionLabel || (option => option.name || option.short_name || '')

	let selected
	if (isMulti) {
		const ids = value || []
		selected = list.filter(option => ids.includes(option.id))
	} else {
		selected = list.find(option => option.id === value) || (allLabel ? list[0] : null)
	}

	function handleChange(option) {
		if (isMulti) onChange((option || []).map(item => item.id))
		else onChange(option ? option.id : -1)
	}

	return (
		<Select
			options={list}
			value={selected}
			onChange={handleChange}
			isMulti={isMulti}
			isDisabled={isDisabled}
			isClearable={isClearable}
			closeMenuOnSelect={!isMulti}
			getOptionLabel={labelOf}
			getOptionValue={option => option.id}
			placeholder={placeholder || (isMulti ? (allLabel || 'Все') : 'Выберите')}
			noOptionsMessage={() => 'Нет вариантов'}
			styles={selectStyles}
			menuPortalTarget={document.body}
		/>
	)
}

// Поле фильтра: подпись сверху, контрол снизу. wide — поле на две колонки сетки
export function FilterField({ label, wide = false, children }) {
	return (
		<div className={`af-field${wide ? ' af-field-wide' : ''}`}>
			<div className="af-label">{label}</div>
			{children}
		</div>
	)
}

// Стандартные поля «Филиал», «Тип заказа», «Источник» — одинаковые на всех экранах продаж.
// options — результат useAnalyticsOptions(['sources']). Поле убирается флагом (branch={false}).
// onPosChange — если экрану при смене филиала надо сбросить зависимые поля (склад, курьер).
export function StandardFields({ filters, options, branch = true, orderType = true, source = true, onPosChange }) {
	const { draft, patch } = filters
	return (
		<>
			{branch && (
				<FilterField label="Филиал">
					<FilterSelect
						options={options.poses}
						allLabel="Все филиалы"
						value={draft.pos_id}
						onChange={(id) => onPosChange ? onPosChange(id) : patch({ pos_id: id })}
					/>
				</FilterField>
			)}
			{orderType && (
				<FilterField label="Тип заказа">
					<FilterSelect isMulti options={options.orderTypes} allLabel="Все типы" value={draft.order_type_ids} onChange={(ids) => patch({ order_type_ids: ids })} />
				</FilterField>
			)}
			{source && (
				<FilterField label="Источник">
					<FilterSelect isMulti options={options.sources} allLabel="Все источники" value={draft.source_ids} onChange={(ids) => patch({ source_ids: ids })} />
				</FilterField>
			)}
		</>
	)
}

// Сегментированный переключатель в стиле панели (группировка, режим отображения)
export function FilterSegment({ options, value, onChange }) {
	return (
		<div className="af-segment">
			{options.map(option => (
				<button
					type="button"
					key={option.id}
					className={`af-chip${option.id === value ? ' active' : ''}`}
					onClick={() => onChange(option.id)}
				>
					{option.icon && <i className={`uil ${option.icon}`}></i>}
					{option.label}
				</button>
			))}
		</div>
	)
}

function PeriodPicker({ draft, patch, presets, inline }) {
	function selectPreset(presetId) {
		if (presetId === 'custom') {
			patch({ period: 'custom' })
			return
		}
		patch(buildPeriod(presetId))
	}

	return (
		<div className="af-period">
			{presets && <div className="af-segment">
				{presets.map(presetId => (
					<button
						type="button"
						key={presetId}
						className={`af-chip${draft.period === presetId ? ' active' : ''}`}
						onClick={() => selectPreset(presetId)}
					>
						{presetId === 'custom' && <i className="uil uil-calendar-alt"></i>}
						{PERIOD_PRESETS[presetId]}
					</button>
				))}
			</div>}
			{inline && <div className="af-inline">{inline}</div>}
			{presets && draft.period === 'custom' && (
				<div className="af-range" style={{ minWidth: '260px' }}>
					<PeriodPickerField
						startDate={draft.start_date}
						endDate={draft.end_date}
						onChange={(period) => patch({ ...period, period: 'custom' })}
						withTime={false}
						presets={[]}
						minDate={returnMinDate(new Date(), ANALYTICS_DATE_RANGE_DAYS)}
						maxDate={new Date()}
					/>
				</div>
			)}
		</div>
	)
}

/**
 * Единая панель фильтров экрана аналитики.
 *
 * filters — результат useAnalyticsFilters. presets — пресеты периода (false — экран без периода).
 * children — поля (FilterField). actions — кнопки справа в шапке (Excel и т.п.).
 * tabs / activeTab / onTabChange — вкладки отчёта под фильтрами.
 */
function AnalyticsFilterBar({
	title,
	icon,
	subtitle,
	filters,
	presets = DEFAULT_PRESET_IDS,
	actions,
	inline,
	tabsOnTop = false,
	children,
	tabs,
	activeTab,
	onTabChange,
}) {
	const { draft, applied, patch, apply, reset, isDirty, isDefault } = filters
	const hasPeriod = presets && 'period' in draft
	const periodLabel = hasPeriod ? formatPeriodLabel(applied.start_date, applied.end_date) : ''
	const invalidRange = hasPeriod && draft.start_date > draft.end_date

	return (
		<div className="card af-card mb-3">
			<div className="card-body">
				<div className="af-header">
					<div className="af-title">
						{icon && (
							<div className="af-title-icon"><i className={`uil ${icon}`}></i></div>
						)}
						<div>
							<h4 className="mb-0">{title}</h4>
							<div className="af-subtitle">
								{periodLabel && <span><i className="uil uil-calendar-alt"></i>{periodLabel}</span>}
								{subtitle && <span>{subtitle}</span>}
							</div>
						</div>
					</div>
					{/* Кнопки всегда справа от заголовка: их место не зависит от числа полей экрана */}
					<div className="af-actions">
						{actions}
						{!isDefault && (
							<button type="button" className="btn af-reset" onClick={() => reset()} title="Вернуть фильтры по умолчанию">
								<i className="uil uil-times"></i>
								Сбросить
							</button>
						)}
						<button
							type="button"
							className={`btn af-apply${isDirty ? ' dirty' : ''}`}
							disabled={invalidRange}
							onClick={() => apply()}
							title={invalidRange ? 'Дата начала позже даты окончания' : undefined}
						>
							<i className={`uil ${isDirty ? 'uil-check' : 'uil-sync'}`}></i>
							{isDirty ? 'Применить' : 'Обновить'}
						</button>
					</div>
				</div>

				{tabsOnTop && tabs && tabs.length > 0 && (
					<div className="af-tabs af-tabs-top">
						{tabs.map(tab => (
							<button
								type="button"
								key={tab.id}
								className={`af-tab${tab.id === activeTab ? ' active' : ''}`}
								onClick={() => onTabChange(tab.id)}
							>
								{tab.icon && <i className={`uil ${tab.icon}`}></i>}
								{tab.label}
							</button>
						))}
					</div>
				)}
				{(hasPeriod || inline) && (
					<div className="af-row">
						<PeriodPicker draft={draft} patch={patch} presets={hasPeriod ? presets : false} inline={inline} />
					</div>
				)}

				{children && (
					<div className="af-row af-grid">
						{children}
					</div>
				)}
				{!tabsOnTop && tabs && tabs.length > 0 && (
					<div className="af-tabs">
						{tabs.map(tab => (
							<button
								type="button"
								key={tab.id}
								className={`af-tab${tab.id === activeTab ? ' active' : ''}`}
								onClick={() => onTabChange(tab.id)}
							>
								{tab.icon && <i className={`uil ${tab.icon}`}></i>}
								{tab.label}
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	)
}

export default AnalyticsFilterBar
